import { z } from "zod";
/**
 * Credits meter the receipt-scanning feature. Users earn credits by watching
 * AdMob rewarded videos (mobile only) and spend them per scan. The balance and
 * ledger live in the `UserData` single table (see docs/receipt-scanning.md);
 * mutations are atomic and never client-trusted.
 *
 * A "credit" is a whole unit (not a money amount), so counts are non-negative
 * integers and deltas are signed integers.
 */
/**
 * Fixed credit prices/rewards, shared by client and server so both agree on the
 * cost of a scan and the reward for an ad without a round-trip. Tunable here
 * without a schema change.
 */
export const CREDIT_COSTS = {
    /** Credits consumed by one receipt scan. */
    SCAN_COST: 1,
    /** Credits granted for one completed rewarded-video view. */
    AD_REWARD: 5,
    /** Credits seeded to a brand-new user so they can try the feature. */
    SIGNUP_GRANT: 3,
};
/** Kind of credit ledger movement. */
export const CreditTxnTypeSchema = z.enum([
    "EARN_AD",
    "SPEND_SCAN",
    "REFUND",
    "SIGNUP_GRANT",
    // A manual balance adjustment made by an admin via the support console
    // (`POST /admin/users/{userId}/credits`). The signed delta can be positive
    // (grant) or negative (deduct); the ledger entry is the audit record.
    "ADMIN_ADJUST",
]);
/** One append-only ledger entry. */
export const CreditLedgerEntrySchema = z.strictObject({
    id: z.string().uuid(),
    type: CreditTxnTypeSchema,
    /** Signed change in credits: positive for earn/refund, negative for spend. */
    delta: z.number().int(),
    /** Balance immediately after this entry was applied. */
    balanceAfter: z.number().int().nonnegative(),
    /**
     * Free-text note recorded with the movement, for the audit trail. Present
     * only on `ADMIN_ADJUST` entries where the admin supplied a reason; the
     * automated movements (earn/spend/refund/signup) never set it.
     */
    reason: z.string().min(1).max(280).optional(),
    createdAt: z.string().datetime(),
});
/** Response of `GET /users/me/credits`: current balance + recent ledger. */
export const CreditBalanceSchema = z.strictObject({
    balance: z.number().int().nonnegative(),
    ledger: z.array(CreditLedgerEntrySchema),
});
